/**
 * IndexedDB utility for offline storage in RDM App
 * Handles product cache and pending orders
 */

const DB_NAME = 'rdm_app_offline';
// v2: added visit lifecycle stores for #1471 (day/time/task/survey/file/sync queues)
// v3: added visit_singletons for small key/value visit-level state (useVisitTask)
// v4: added expense_log_queue for offline-first expenses (#1513)
// v5: added event_queue for rep activity event stream (#1950)
// v6: added dependsOn index on visit_sync_queue for parent-op ordering
//     in offline-first sync migration (#1983, parent localId reconciliation)
const DB_VERSION = 6;

// Store names
export const STORES = {
  PRODUCTS: 'products',
  PENDING_ORDERS: 'pending_orders',
  SYNC_STATUS: 'sync_status',
  // Visit lifecycle stores — see visitDB.ts for typed CRUD
  VISIT_DAY_SESSIONS: 'visit_day_sessions',
  VISIT_TIME_ENTRIES: 'visit_time_entries',
  VISIT_TASK_SESSIONS: 'visit_task_sessions',
  VISIT_SURVEY_RESPONSES: 'visit_survey_responses',
  VISIT_FILE_QUEUE: 'visit_file_queue',
  VISIT_SYNC_QUEUE: 'visit_sync_queue',
  VISIT_SINGLETONS: 'visit_singletons',
  // Expenses queue — see expensesDB.ts for typed CRUD (#1513)
  EXPENSE_LOG_QUEUE: 'expense_log_queue',
  // Rep activity event stream queue — see eventLog.ts (#1950)
  EVENT_QUEUE: 'event_queue',
} as const;

// Types
export interface CachedProduct {
  productid: number;
  upc: string;
  description: string;
  codenbr: string;
  cost: number;
  packsize: string;
  size: string;
  uom: string;
  brand: string;
  projectId: number;
  storeId: number;
  distributorId?: number;
  /** #2022: order quantities must be multiples of this (default 1 == no constraint). */
  order_increment?: number;
  cachedAt: string;
}

export interface PendingOrderItem {
  productid: number;
  upc: string;
  description: string;
  orderqty: number;
  cost: number;
  packsize: string;
}

export interface PendingOrder {
  id: string;
  storeId: number;
  storeName: string;
  projectId: number;
  callId?: number;
  associateId: number;
  distributorId: number;
  items: PendingOrderItem[];
  totalCost: number;
  createdAt: string;
  status: 'pending' | 'syncing' | 'failed' | 'synced';
  syncAttempts: number;
  lastSyncAttempt?: string;
  syncError?: string;
  serverId?: number;
}

export interface SyncStatus {
  key: string;
  lastSync: string;
  status: 'idle' | 'syncing' | 'error';
  error?: string;
}

let dbInstance: IDBDatabase | null = null;
// In-flight open. Without it, N concurrent callers each fire their own
// indexedDB.open(): only the last result is cached in dbInstance and the rest
// become connections nobody can close — which is precisely what leaves a
// deleteDatabase() blocked forever (#1964 / diag #43).
let openRequest: Promise<IDBDatabase> | null = null;

/**
 * Close the cached connection so a subsequent indexedDB.deleteDatabase()
 * isn't blocked. Used by Hard Reset Day (#1964) before nuking IDB —
 * without this, iOS Safari leaves the deletion pending and the next
 * page load reads the old day session back as if nothing happened.
 */
export const closeDatabase = (): void => {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
  openRequest = null;
};

/**
 * Clear the CONTENTS of every object store in one readwrite transaction.
 *
 * This is the reliable wipe for Hard Reset on iOS Safari (#1964 / diag #43):
 * indexedDB.deleteDatabase() BLOCKS while any connection is open (the sync
 * engine reopens constantly), hits the Hard Reset timeout, and silently
 * no-ops — so the stuck day/session/queue rows survive the "reset". Clearing
 * stores is a normal transaction that succeeds with live connections, so the
 * data is actually gone afterward. Returns the names of the cleared stores.
 */
export const clearAllStores = (): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(new Error('clearAllStores: failed to open IndexedDB'));
    request.onsuccess = () => {
      const db = request.result;
      const names = Array.from(db.objectStoreNames);
      if (names.length === 0) {
        db.close();
        resolve([]);
        return;
      }
      let tx: IDBTransaction;
      try {
        tx = db.transaction(names, 'readwrite');
      } catch (err) {
        db.close();
        reject(err instanceof Error ? err : new Error('clearAllStores: could not start transaction'));
        return;
      }
      for (const name of names) tx.objectStore(name).clear();
      tx.oncomplete = () => {
        db.close();
        if (dbInstance === db) dbInstance = null;
        resolve(names);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error || new Error('clearAllStores: transaction error'));
      };
      tx.onabort = () => {
        db.close();
        reject(tx.error || new Error('clearAllStores: transaction aborted'));
      };
    };
  });
};

/**
 * Open and initialize the database
 */
export const openDatabase = (): Promise<IDBDatabase> => {
  if (dbInstance) return Promise.resolve(dbInstance);
  if (openRequest) return openRequest;

  openRequest = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      openRequest = null;
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      // If a deleteDatabase() fires elsewhere (e.g. Hard Reset Day #1964),
      // a held-open connection blocks it indefinitely. Close on
      // versionchange so the deletion can proceed.
      dbInstance.onversionchange = () => {
        dbInstance?.close();
        dbInstance = null;
        openRequest = null;
      };
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Products store - indexed by UPC and projectId
      if (!db.objectStoreNames.contains(STORES.PRODUCTS)) {
        const productStore = db.createObjectStore(STORES.PRODUCTS, {
          keyPath: ['productid', 'storeId'],
        });
        productStore.createIndex('upc', 'upc', { unique: false });
        productStore.createIndex('projectId', 'projectId', { unique: false });
        productStore.createIndex('storeId', 'storeId', { unique: false });
        productStore.createIndex('upc_storeId', ['upc', 'storeId'], { unique: false });
      }

      // Pending orders store
      if (!db.objectStoreNames.contains(STORES.PENDING_ORDERS)) {
        const orderStore = db.createObjectStore(STORES.PENDING_ORDERS, {
          keyPath: 'id',
        });
        orderStore.createIndex('status', 'status', { unique: false });
        orderStore.createIndex('storeId', 'storeId', { unique: false });
        orderStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Sync status store
      if (!db.objectStoreNames.contains(STORES.SYNC_STATUS)) {
        db.createObjectStore(STORES.SYNC_STATUS, { keyPath: 'key' });
      }

      // Visit lifecycle stores (v2) — #1471
      // Each `if (!contains)` guard makes the upgrade handler idempotent for
      // both fresh installs and browsers upgrading from v1.
      if (!db.objectStoreNames.contains(STORES.VISIT_DAY_SESSIONS)) {
        const daySessionStore = db.createObjectStore(STORES.VISIT_DAY_SESSIONS, {
          keyPath: 'id',
          autoIncrement: true,
        });
        daySessionStore.createIndex('associateId', 'associateId', { unique: false });
        daySessionStore.createIndex('status', 'status', { unique: false });
        daySessionStore.createIndex('startTime', 'startTime', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.VISIT_TIME_ENTRIES)) {
        const timeEntryStore = db.createObjectStore(STORES.VISIT_TIME_ENTRIES, {
          keyPath: 'id',
          autoIncrement: true,
        });
        timeEntryStore.createIndex('daySessionId', 'daySessionId', { unique: false });
        timeEntryStore.createIndex('type', 'type', { unique: false });
        timeEntryStore.createIndex('storeId', 'storeId', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.VISIT_TASK_SESSIONS)) {
        const taskSessionStore = db.createObjectStore(STORES.VISIT_TASK_SESSIONS, {
          keyPath: 'uuid',
        });
        taskSessionStore.createIndex('timeEntryId', 'timeEntryId', { unique: false });
        taskSessionStore.createIndex('callLogId', 'callLogId', { unique: false });
        taskSessionStore.createIndex('status', 'status', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.VISIT_SURVEY_RESPONSES)) {
        // Keyed by taskUUID — one response bundle per task
        db.createObjectStore(STORES.VISIT_SURVEY_RESPONSES, {
          keyPath: 'taskUUID',
        });
      }

      if (!db.objectStoreNames.contains(STORES.VISIT_FILE_QUEUE)) {
        const fileQueueStore = db.createObjectStore(STORES.VISIT_FILE_QUEUE, {
          keyPath: 'id',
          autoIncrement: true,
        });
        fileQueueStore.createIndex('taskUUID', 'taskUUID', { unique: false });
        fileQueueStore.createIndex('type', 'type', { unique: false });
        fileQueueStore.createIndex('syncStatus', 'syncStatus', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.VISIT_SYNC_QUEUE)) {
        const syncQueueStore = db.createObjectStore(STORES.VISIT_SYNC_QUEUE, {
          keyPath: 'id',
          autoIncrement: true,
        });
        syncQueueStore.createIndex('status', 'status', { unique: false });
        syncQueueStore.createIndex('priority', 'priority', { unique: false });
        syncQueueStore.createIndex('createdAt', 'createdAt', { unique: false });
        // v6: parent-op ordering for offline-first sync (#1983)
        syncQueueStore.createIndex('dependsOn', 'dependsOn', { unique: false });
      } else if (event.oldVersion < 6) {
        // v6 upgrade for installs that already had visit_sync_queue: add
        // the dependsOn index in place without recreating the store.
        const tx = (event.target as IDBOpenDBRequest).transaction!;
        const store = tx.objectStore(STORES.VISIT_SYNC_QUEUE);
        if (!Array.from(store.indexNames).includes('dependsOn')) {
          store.createIndex('dependsOn', 'dependsOn', { unique: false });
        }
      }

      // v3: small key/value store for visit-level singleton state
      // (current store visit metadata). See visitDB.ts and useVisitTask.
      if (!db.objectStoreNames.contains(STORES.VISIT_SINGLETONS)) {
        db.createObjectStore(STORES.VISIT_SINGLETONS, { keyPath: 'key' });
      }

      // v4: expense log queue — offline-first expenses (#1513). Stores the
      // pending POST payload + receipt as a Blob until the sync engine
      // pushes it to /api/associates/expense-logs/.
      if (!db.objectStoreNames.contains(STORES.EXPENSE_LOG_QUEUE)) {
        const expenseStore = db.createObjectStore(STORES.EXPENSE_LOG_QUEUE, {
          keyPath: 'id',
        });
        expenseStore.createIndex('syncStatus', 'syncStatus', { unique: false });
        expenseStore.createIndex('expenseDate', 'expenseDate', { unique: false });
        expenseStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // v5: rep activity event queue (#1950). Append-only event log written
      // by every state-changing UI path. Sync engine flushes pending rows
      // to POST /api/projects/events/ and deletes them on 200 ack.
      // Keyed by client_event_id (UUID v4) so the server's UNIQUE index
      // can dedupe retries idempotently.
      if (!db.objectStoreNames.contains(STORES.EVENT_QUEUE)) {
        const eventStore = db.createObjectStore(STORES.EVENT_QUEUE, {
          keyPath: 'client_event_id',
        });
        eventStore.createIndex('sync_status', 'sync_status', { unique: false });
        eventStore.createIndex('queued_at', 'queued_at', { unique: false });
        eventStore.createIndex('event_type', 'event_type', { unique: false });
      }
    };
  });

  return openRequest;
};

/**
 * Generic add/put operation
 */
export const put = async <T>(storeName: string, data: T): Promise<void> => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(data);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Generic bulk put operation
 */
export const putMany = async <T>(storeName: string, items: T[]): Promise<void> => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);

    let completed = 0;
    const total = items.length;

    if (total === 0) {
      resolve();
      return;
    }

    items.forEach((item) => {
      const request = store.put(item);
      request.onsuccess = () => {
        completed++;
        if (completed === total) {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });

    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Generic get operation
 */
export const get = async <T>(storeName: string, key: IDBValidKey): Promise<T | undefined> => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get all items from a store
 */
export const getAll = async <T>(storeName: string): Promise<T[]> => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get items by index
 */
export const getByIndex = async <T>(
  storeName: string,
  indexName: string,
  value: IDBValidKey
): Promise<T[]> => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(value);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Delete by key
 */
export const remove = async (storeName: string, key: IDBValidKey): Promise<void> => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all items from a store
 */
export const clear = async (storeName: string): Promise<void> => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear products for a specific store
 */
export const clearProductsForStore = async (storeId: number): Promise<void> => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.PRODUCTS, 'readwrite');
    const store = transaction.objectStore(STORES.PRODUCTS);
    const index = store.index('storeId');
    const request = index.openCursor(IDBKeyRange.only(storeId));

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = () => reject(request.error);
  });
};

/**
 * Count items in a store
 */
export const count = async (storeName: string): Promise<number> => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.count();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Generate a UUID for pending orders
 */
export const generateId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
